import type { Page, LayoutGroup } from "./schema";
import type { SceneNode } from "./scene";
import { intersectRects, type Rect } from "./production";

/** Validate even hidden groups; a hidden cycle must never reach recursive layout. */
export function assertLayoutGroups(page: Page): void {
  const groups = new Map(
    (page.layoutGroups || []).map((group) => [group.id, group]),
  );
  if (groups.size !== (page.layoutGroups || []).length)
    throw new Error("Duplicate layout group identity");
  for (const group of groups.values()) {
    const path = new Set<string>();
    let next: LayoutGroup | undefined = group;
    while (next) {
      if (path.has(next.id)) throw new Error("Layout group cycle");
      path.add(next.id);
      if (path.size > 24) throw new Error("Layout groups exceed 24 levels");
      if (next.parentId && !groups.has(next.parentId))
        throw new Error("Unknown parent layout group");
      next = next.parentId ? groups.get(next.parentId) : undefined;
    }
  }
  for (const node of page.nodes) {
    if (node.layoutGroupId && !groups.has(node.layoutGroupId))
      throw new Error("Unknown layout group");
    if (
      node.layoutGroupId &&
      (node.placement !== "fixed" ||
        node.anchor !== "top-left" ||
        node.stretch !== "none")
    )
      throw new Error(
        "Grouped layers use fixed placement without page anchors or stretch",
      );
  }
}
interface Box {
  width: number;
  height: number;
  x: number;
  y: number;
  order: number;
  margins: LayoutGroup["margins"];
  node?: SceneNode;
  group?: LayoutGroup;
  children?: Box[];
}
/** Resolve ancestry before text evaluation, font measurement or block packing. */
export function layoutGroupVisibility(
  page: Page,
  visible: (group: LayoutGroup) => boolean,
): Map<string, boolean> {
  assertLayoutGroups(page);
  const groups = new Map(
    (page.layoutGroups || []).map((group) => [group.id, group]),
  );
  const result = new Map<string, boolean>();
  const visit = (id: string): boolean => {
    if (result.has(id)) return result.get(id)!;
    const group = groups.get(id)!;
    const shown = (!group.parentId || visit(group.parentId)) && visible(group);
    result.set(id, shown);
    return shown;
  };
  for (const id of groups.keys()) visit(id);
  return result;
}
const alignment = (value: LayoutGroup["align"]) =>
  value === "center" ? 0.5 : value === "end" ? 1 : 0;

/** Measure bottom-up and place top-down. Hidden children consume neither space nor gaps. */
export function layoutPageGroups(
  page: Page,
  nodes: SceneNode[],
  visible: (group: LayoutGroup) => boolean,
): SceneNode[] {
  if (!page.layoutGroups?.length) return nodes;
  assertLayoutGroups(page);
  const members = new Map<string, Box[]>();
  const result = new Map(nodes.map((node) => [node.id, node]));
  for (const node of nodes)
    if (node.layoutGroupId) {
      const entries = members.get(node.layoutGroupId) || [];
      entries.push({
        node,
        width: node.width,
        height: node.height,
        x: node.x,
        y: node.y,
        order: node.layoutOrder || 0,
        margins: node.layoutMargins || { top: 0, right: 0, bottom: 0, left: 0 },
      });
      members.set(node.layoutGroupId, entries);
      result.delete(node.id);
    }
  const nested = new Map<string | undefined, LayoutGroup[]>();
  for (const group of page.layoutGroups) {
    const list = nested.get(group.parentId) || [];
    list.push(group);
    nested.set(group.parentId, list);
  }
  const measure = (group: LayoutGroup): Box | undefined => {
    if (!visible(group)) return;
    const children = [
      ...(members.get(group.id) || []),
      ...(nested.get(group.id) || []).flatMap((child) => {
        const box = measure(child);
        return box ? [box] : [];
      }),
    ].sort((a, b) => a.order - b.order);
    if (!children.length) return;
    let width = 0,
      height = 0;
    for (const [index, child] of children.entries()) {
      const m = child.margins,
        w = child.width + m.left + m.right,
        h = child.height + m.top + m.bottom;
      if (group.direction === "vertical") {
        width = Math.max(width, w);
        height += h + (index ? group.gap : 0);
      } else if (group.direction === "horizontal") {
        width += w + (index ? group.gap : 0);
        height = Math.max(height, h);
      } else {
        width = Math.max(
          width,
          child.x +
            w -
            (child.group ? alignment(child.group.anchorX) * child.width : 0),
        );
        height = Math.max(
          height,
          child.y +
            h -
            (child.group ? alignment(child.group.anchorY) * child.height : 0),
        );
      }
    }
    const constrained = (value: number, minimum?: number, maximum?: number) =>
      Math.min(maximum ?? Infinity, Math.max(minimum ?? 0, value));
    return {
      group,
      children,
      width: constrained(group.width ?? width, group.minWidth, group.maxWidth),
      height: constrained(
        group.height ?? height,
        group.minHeight,
        group.maxHeight,
      ),
      x: group.x,
      y: group.y,
      order: group.order,
      margins: group.margins,
    };
  };
  const place = (box: Box, x: number, y: number, inheritedClip?: Rect) => {
    x += box.group?.offsetX || 0;
    y += box.group?.offsetY || 0;
    if (
      ![x, y, box.width, box.height].every(Number.isFinite) ||
      Math.abs(x) > 14400 ||
      Math.abs(y) > 14400 ||
      box.width > 14400 ||
      box.height > 14400
    )
      throw new Error("Layout group exceeds document geometry limits");
    if (box.node) {
      const clip = inheritedClip
        ? box.node.clip
          ? intersectRects(inheritedClip, box.node.clip)
          : inheritedClip
        : box.node.clip;
      result.set(box.node.id, { ...box.node, x, y, ...(clip ? { clip } : {}) });
      return;
    }
    const group = box.group!;
    const bounds = { x, y, width: box.width, height: box.height };
    const clip = group.clipContent
      ? inheritedClip
        ? intersectRects(inheritedClip, bounds)
        : bounds
      : inheritedClip;
    let cursor = 0;
    for (const [index, child] of box.children!.entries()) {
      const m = child.margins;
      let cx = child.x + m.left,
        cy = child.y + m.top;
      if (group.direction === "vertical") {
        cursor += (index ? group.gap : 0) + m.top;
        cx =
          m.left +
          Math.max(0, box.width - child.width - m.left - m.right) *
            alignment(group.align);
        cy = cursor;
        cursor += child.height + m.bottom;
      } else if (group.direction === "horizontal") {
        cursor += (index ? group.gap : 0) + m.left;
        cy =
          m.top +
          Math.max(0, box.height - child.height - m.top - m.bottom) *
            alignment(group.align);
        cx = cursor;
        cursor += child.width + m.right;
      } else if (child.group) {
        cx -= alignment(child.group.anchorX) * child.width;
        cy -= alignment(child.group.anchorY) * child.height;
      }
      place(child, x + cx, y + cy, clip);
    }
  };
  for (const group of nested.get(undefined) || []) {
    const box = measure(group);
    if (box)
      place(
        box,
        box.x - alignment(group.anchorX) * box.width,
        box.y - alignment(group.anchorY) * box.height,
      );
  }
  return nodes.flatMap((node) =>
    result.has(node.id) ? [result.get(node.id)!] : [],
  );
}

/** Outermost locked ancestor controls movement, including nested descendants. */
export function movementLayoutGroup(
  page: Page,
  nodeId: string,
): LayoutGroup | undefined {
  const groups = new Map(
    (page.layoutGroups || []).map((group) => [group.id, group]),
  );
  let next = page.nodes.find((node) => node.id === nodeId)?.layoutGroupId;
  let selected: LayoutGroup | undefined;
  const visited = new Set<string>();
  while (next && !visited.has(next)) {
    visited.add(next);
    const group = groups.get(next);
    if (!group) break;
    if (group.moveTogether !== false) selected = group;
    next = group.parentId;
  }
  return selected;
}
export function layoutGroupMemberIds(page: Page, groupId: string): Set<string> {
  const groups = new Set([groupId]);
  for (let depth = 0; depth < 24; depth++)
    for (const group of page.layoutGroups || [])
      if (group.parentId && groups.has(group.parentId)) groups.add(group.id);
  return new Set(
    page.nodes
      .filter((node) => node.layoutGroupId && groups.has(node.layoutGroupId))
      .map((node) => node.id),
  );
}
export function translatedLayoutGroups(
  page: Page,
  groupId: string,
  dx: number,
  dy: number,
): LayoutGroup[] {
  const parent = page.layoutGroups?.find(
    (group) =>
      group.id ===
      page.layoutGroups?.find((item) => item.id === groupId)?.parentId,
  );
  return (page.layoutGroups || []).map((group) =>
    group.id !== groupId
      ? group
      : parent && parent.direction !== "overlay"
        ? {
            ...group,
            offsetX: (group.offsetX || 0) + dx,
            offsetY: (group.offsetY || 0) + dy,
          }
        : { ...group, x: group.x + dx, y: group.y + dy },
  );
}
